import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pill, AlertTriangle, Activity, ClipboardList, Compass, ChevronRight, Sparkles, Clock, Stethoscope, ShieldCheck } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function extractInsights(records) {
  const flaggedValues = [];
  const diagnoses = [];
  const actions = [];
  records.forEach((record) => {
    const a = record.aiAnalysis || {};
    (a.flaggedValues || []).forEach((v) => {
      if (v.status && v.status !== "normal" && v.name) flaggedValues.push(v);
    });
    const diagList = Array.isArray(a.diagnosis) ? a.diagnosis : a.diagnosis ? [a.diagnosis] : [];
    diagList.forEach((d) => { if (d && !diagnoses.includes(d)) diagnoses.push(d); });
    (a.recommendations || []).forEach((r) => { if (r && !actions.includes(r) && actions.length < 5) actions.push(r); });
  });
  return { flaggedValues: flaggedValues.slice(0, 5), diagnoses: diagnoses.slice(0, 8), actions: actions.slice(0, 3) };
}

export default function HealthInsightsPanel({ records, medConditions, onUpload, patientFilters, onOpenPatientSummary }) {
  const navigate = useNavigate();
  const insights = useMemo(() => extractInsights(records), [records]);
  const meds = medConditions?.medications || [];
  const summaryMembers = (patientFilters || []).filter((p) => p.type === "self" || p.type === "dependent");

  if (records.length === 0) {
    return (
      <Card className="rounded-3xl p-10 text-center border-dashed" data-testid="summary-empty">
        <Sparkles className="h-12 w-12 text-muted-foreground mx-auto" />
        <p className="mt-4 font-medium">No AI insights yet</p>
        <p className="text-sm text-muted-foreground mt-1">Upload your medical documents to get AI-powered health insights.</p>
        <Button className="mt-5 rounded-2xl" onClick={onUpload} data-testid="summary-empty-upload">Upload Document</Button>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => navigate("/vital-map")}
        className="w-full flex items-center gap-3 rounded-3xl border border-indigo-200 bg-indigo-50 p-4 text-left hover:bg-indigo-100 transition-colors"
        data-testid="vital-map-link"
      >
        <Compass className="h-6 w-6 text-indigo-600 shrink-0" />
        <div className="flex-1">
          <p className="font-semibold text-indigo-900">See your organ-by-organ Vital Map</p>
          <p className="text-xs text-indigo-700">Body-wide health map with test suggestions</p>
        </div>
        <ChevronRight className="h-5 w-5 text-indigo-500" />
      </button>

      {summaryMembers.length > 0 && (
        <Card className="rounded-3xl p-5" data-testid="patient-summary-launchers">
          <p className="font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> AI Health Summary</p>
          <p className="text-xs text-muted-foreground mt-0.5">Get a comprehensive AI analysis per family member.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {summaryMembers.map((m) => (
              <Button key={m.id} variant="outline" size="sm" className="rounded-full capitalize" onClick={() => onOpenPatientSummary(m)} data-testid={`patient-summary-${m.id}`}>
                {m.type === "self" ? "👤" : "👥"} {m.name}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {meds.length > 0 && (
        <Card className="rounded-3xl p-5" data-testid="insight-medications">
          <p className="font-semibold flex items-center gap-2"><Pill className="h-4 w-4 text-violet-600" /> My Medications <span className="text-xs text-muted-foreground">({meds.length})</span></p>
          <div className="mt-3 space-y-3">
            {meds.map((m, i) => (
              <div key={i} className="rounded-2xl border border-border p-4" data-testid={`insight-med-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold">{m.name}</p>
                    {m.purpose && <p className="text-sm text-muted-foreground">For: {m.purpose}</p>}
                  </div>
                  {m.active && <span className="text-[10px] font-bold uppercase text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full shrink-0">Active</span>}
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                  {m.dosage && <div className="rounded-xl bg-muted/50 px-3 py-2"><p className="text-[11px] text-muted-foreground">How much</p><p className="font-medium">{m.dosage}</p></div>}
                  {m.frequency && <div className="rounded-xl bg-muted/50 px-3 py-2"><p className="text-[11px] text-muted-foreground">When</p><p className="font-medium">{m.frequency}</p></div>}
                  {m.duration && <div className="rounded-xl bg-muted/50 px-3 py-2"><p className="text-[11px] text-muted-foreground">How long</p><p className="font-medium">{m.duration}</p></div>}
                </div>
                {m.instructions && <p className="text-xs text-muted-foreground mt-2">📝 {m.instructions}</p>}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                  {m.lastPrescribedDate && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Last prescribed {new Date(m.lastPrescribedDate).toLocaleDateString()}</span>}
                  {m.prescribedBy && <span className="flex items-center gap-1"><Stethoscope className="h-3 w-3" /> {m.prescribedBy}</span>}
                </div>
              </div>
            ))}
          </div>
          {medConditions?.disclaimer && (
            <p className="mt-3 text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {medConditions.disclaimer}</p>
          )}
        </Card>
      )}

      {insights.flaggedValues.length > 0 && (
        <Card className="rounded-3xl p-5 border-red-200 bg-red-50/50" data-testid="insight-flagged">
          <p className="font-semibold flex items-center gap-2 text-red-700"><AlertTriangle className="h-4 w-4" /> Values Needing Attention</p>
          <div className="mt-3 space-y-2">
            {insights.flaggedValues.map((v, i) => (
              <div key={i} className="flex items-center justify-between gap-2 rounded-xl bg-white border border-red-100 p-3">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{v.name}</p>
                  <p className="text-xs text-muted-foreground">{v.value}{v.reference ? ` (Normal: ${v.reference})` : ""}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${v.status === "critical" ? "bg-red-200 text-red-800" : v.status === "abnormal" ? "bg-orange-100 text-orange-700" : "bg-amber-100 text-amber-700"}`}>{v.status}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {insights.diagnoses.length > 0 && (
        <Card className="rounded-3xl p-5" data-testid="insight-diagnoses">
          <p className="font-semibold flex items-center gap-2"><Activity className="h-4 w-4 text-teal-600" /> Conditions Mentioned</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {insights.diagnoses.map((d, i) => (
              <span key={i} className="text-sm px-3 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-100">{d}</span>
            ))}
          </div>
        </Card>
      )}

      {insights.actions.length > 0 && (
        <Card className="rounded-3xl p-5" data-testid="insight-actions">
          <p className="font-semibold flex items-center gap-2"><ClipboardList className="h-4 w-4 text-blue-600" /> Recommended Actions</p>
          <div className="mt-3 space-y-2">
            {insights.actions.map((a, i) => (
              <div key={i} className="flex items-start text-sm text-slate-700"><span className="text-blue-500 mr-2">•</span><span className="flex-1">{a}</span></div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
